/**
 * Marketing attribution capture.
 *
 * UTM parameters, ad click ids and the referrer only exist on the *landing*
 * request. Sign-up, assessments and payment happen many navigations later
 * (often after an OAuth/OTP round trip or the payment gateway that strips the
 * query string), so we persist two touches for 90 days:
 *   - first touch  -> acquisition; replaced only by a different Facebook click id
 *   - last touch   -> the latest campaign; replaced only when NEW campaign params arrive
 *
 * Each touch is kept in localStorage AND a first-party cookie (shared by
 * leadxkids.com and www.leadxkids.com), so clearing one store, switching tabs
 * or hopping between the two hostnames does not lose the ad click.
 *
 * No personal data is stored — only campaign tags, click ids and referrer.
 */

const FIRST_TOUCH_KEY = "lx_attribution_first";
const LAST_TOUCH_KEY = "lx_attribution_recent";
/** Pre-90-day session copy; read once for visitors who landed before this change. */
const LEGACY_SESSION_KEY = "lx_attribution_last";
const COOKIE_NAME = "lx_attr";

/** How long a stored touch stays attributable. */
export const ATTRIBUTION_MAX_AGE_MS = 90 * 24 * 60 * 60 * 1000;
/** @deprecated kept for older imports; same 90-day window. */
export const RECENT_TOUCH_MAX_AGE_MS = ATTRIBUTION_MAX_AGE_MS;

export interface Attribution {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
  utm_content?: string;
  gclid?: string;
  /** iOS/app Ads click ids that replace gclid when gclid is unavailable. */
  gbraid?: string;
  wbraid?: string;
  fbclid?: string;
  referrer?: string;
  landing_page?: string;
  captured_at?: string;
}

const UTM_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
  "gclid",
  "gbraid",
  "wbraid",
  "fbclid",
] as const;

/** Google Ads click identifiers, in the precedence order gtag.js uses. */
export const CLICK_ID_KEYS = ["gclid", "gbraid", "wbraid"] as const;

/** Keys of the flat record saved on sign-ups, assessments and payments. */
export const ATTRIBUTION_RECORD_KEYS = [
  ...UTM_KEYS,
  "referrer",
  "landing_page",
  "last_touch_at",
  ...UTM_KEYS.map((k) => `first_${k}` as const),
  "first_referrer",
  "first_landing_page",
  "first_touch_at",
  "first_touch",
  "landing_url",
] as const;

const clean = (v: string | null, max = 200): string | undefined =>
  v ? v.trim().slice(0, max) || undefined : undefined;

function readCurrent(): Attribution {
  const out: Attribution = {};
  try {
    const p = new URLSearchParams(window.location.search);
    for (const key of UTM_KEYS) {
      // Meta click ids are routinely ~190 characters; keep them whole.
      const value = clean(p.get(key), key === "fbclid" ? 500 : 200);
      if (value) out[key] = value;
    }
    const ref = document.referrer;
    // Ignore self-referrals — they are internal navigation, not an acquisition source.
    if (ref && !ref.startsWith(window.location.origin) && !isOwnHost(ref)) out.referrer = safeAttributionUrl(ref);
    out.landing_page = safeAttributionUrl(window.location.href)?.slice(0, 300);
    out.captured_at = new Date().toISOString();
  } catch {
    /* URL access can throw in locked-down browsers */
  }
  return out;
}

function isOwnHost(url: string): boolean {
  try {
    return /(^|\.)leadxkids\.com$/i.test(new URL(url).hostname);
  } catch {
    return false;
  }
}

/** Campaign params (UTMs or ad click ids) present on this URL. */
function hasCampaign(a: Attribution): boolean {
  return UTM_KEYS.some((k) => Boolean(a[k]));
}

/** True when the payload carries a campaign or an external referrer. */
export function safeAttributionUrl(raw: string): string | undefined {
  try {
    const url = new URL(raw);
    if (!['https:', 'http:'].includes(url.protocol)) return undefined;
    // Never retain arbitrary query parameters, fragments or URL credentials.
    const path = /@|%40|\+?\d[\d -]{7,}/i.test(url.pathname) ? '/' : url.pathname;
    return `${url.origin}${path}`.slice(0, 500);
  } catch { return undefined; }
}

function fresh(a: Attribution | null | undefined): Attribution | null {
  if (!a || typeof a !== "object") return null;
  const at = a.captured_at ? Date.parse(a.captured_at) : NaN;
  return Number.isFinite(at) && at <= Date.now() && Date.now() - at < ATTRIBUTION_MAX_AGE_MS ? a : null;
}

function readStore(store: Storage | undefined, key: string): Attribution | null {
  try {
    const raw = store?.getItem(key);
    return raw ? (JSON.parse(raw) as Attribution) : null;
  } catch {
    return null;
  }
}

function writeStore(store: Storage | undefined, key: string, value: Attribution): void {
  try {
    store?.setItem(key, JSON.stringify(value));
  } catch {
    /* quota / private mode — attribution is best effort */
  }
}

function safeLocal(): Storage | undefined {
  try {
    return window.localStorage;
  } catch {
    return undefined;
  }
}

function safeSession(): Storage | undefined {
  try {
    return window.sessionStorage;
  } catch {
    return undefined;
  }
}

/** `.leadxkids.com` on the real site so apex and www share cookies; host-only elsewhere. */
function cookieDomain(): string {
  const host = window.location.hostname;
  return /(^|\.)leadxkids\.com$/i.test(host) ? "; domain=.leadxkids.com" : "";
}

function cookieSuffix(maxAgeMs: number): string {
  const secure = window.location.protocol === "https:" ? "; Secure" : "";
  return `; path=/; max-age=${Math.floor(maxAgeMs / 1000)}; SameSite=Lax${cookieDomain()}${secure}`;
}

export function readCookie(name: string): string | undefined {
  try {
    for (const part of document.cookie.split(/;\s*/)) {
      const idx = part.indexOf("=");
      if (idx > 0 && part.slice(0, idx) === name) return decodeURIComponent(part.slice(idx + 1));
    }
  } catch {
    /* cookies disabled */
  }
  return undefined;
}

function readCookieTouches(): { first: Attribution | null; last: Attribution | null } {
  try {
    const raw = readCookie(COOKIE_NAME);
    if (!raw) return { first: null, last: null };
    const parsed = JSON.parse(raw) as { f?: Attribution; l?: Attribution };
    return { first: parsed.f ?? null, last: parsed.l ?? null };
  } catch {
    return { first: null, last: null };
  }
}

function writeCookieTouches(first: Attribution | null, last: Attribution | null): void {
  try {
    const value = encodeURIComponent(JSON.stringify({ f: first ?? undefined, l: last ?? undefined }));
    document.cookie = `${COOKIE_NAME}=${value}${cookieSuffix(ATTRIBUTION_MAX_AGE_MS)}`;
  } catch {
    /* cookies disabled */
  }
}

/** Stored first/last touches (90-day window), merged from localStorage and the cookie. */
export function getAttribution(): { first: Attribution | null; last: Attribution | null } {
  if (typeof window === "undefined") return { first: null, last: null };
  const local = safeLocal();
  const jar = readCookieTouches();
  const first = fresh(readStore(local, FIRST_TOUCH_KEY)) ?? fresh(jar.first);
  const last =
    fresh(readStore(local, LAST_TOUCH_KEY)) ??
    fresh(jar.last) ??
    fresh(readStore(safeSession(), LEGACY_SESSION_KEY));
  return { first, last };
}

/**
 * Meta's `_fbc` click cookie in the standard `fb.1.<ms timestamp>.<fbclid>`
 * format. Written only when the landing URL carries an fbclid the cookie does
 * not already hold, so the pixel's own value is never replaced for the same
 * click. `_fbp` is left entirely to the pixel.
 */
export function ensureFbcCookie(fbclid: string | undefined, now = Date.now()): void {
  if (!fbclid) return;
  try {
    const existing = readCookie("_fbc");
    if (existing && existing.split(".").slice(3).join(".") === fbclid) return;
    document.cookie = `_fbc=fb.1.${now}.${fbclid}${cookieSuffix(ATTRIBUTION_MAX_AGE_MS)}`;
  } catch {
    /* cookies disabled */
  }
}

/** Pixel browser ids for server-side event matching. */
export function metaBrowserIds(): { fbp?: string; fbc?: string } {
  if (typeof window === "undefined") return {};
  const fbp = readCookie("_fbp");
  let fbc = readCookie("_fbc");
  if (!fbc) {
    const { first, last } = getAttribution();
    const touch = last?.fbclid ? last : first?.fbclid ? first : null;
    if (touch?.fbclid) {
      const at = Date.parse(touch.captured_at ?? "") || Date.now();
      fbc = `fb.1.${at}.${touch.fbclid}`;
    }
  }
  return { ...(fbp ? { fbp } : {}), ...(fbc ? { fbc } : {}) };
}

/**
 * Capture attribution for this page load. Call once at app startup, before any
 * router navigation can strip the query string.
 */
export function captureAttribution(): void {
  if (typeof window === "undefined") return;
  const current = readCurrent();
  const local = safeLocal();
  let { first, last } = getAttribution();
  let changed = false;

  if (!first || (current.fbclid && current.fbclid !== first.fbclid)) {
    first = current;
    changed = true;
  }
  if (hasCampaign(current)) {
    last = current;
    changed = true;
  }

  // Re-sync both stores whenever either is missing a touch the other has.
  const localFirst = fresh(readStore(local, FIRST_TOUCH_KEY));
  const localLast = fresh(readStore(local, LAST_TOUCH_KEY));
  if (first && (changed || !localFirst)) writeStore(local, FIRST_TOUCH_KEY, first);
  if (last && (changed || !localLast)) writeStore(local, LAST_TOUCH_KEY, last);
  if (first || last) {
    const jar = readCookieTouches();
    if (changed || !fresh(jar.first) !== !first || !fresh(jar.last) !== !last) writeCookieTouches(first, last);
  }

  ensureFbcCookie(current.fbclid);
}

/** Stored click ids for the current visit, empty when the visit was not from Ads. */
export function storedClickIds(): Record<string, string> {
  const { first, last } = getAttribution();
  const touch = last ?? first;
  const out: Record<string, string> = {};
  if (!touch) return out;
  for (const key of CLICK_ID_KEYS) {
    const value = touch[key];
    if (value) out[key] = value;
  }
  return out;
}

/**
 * Flat attribution fields for a GA4 dataLayer push.
 *
 * Last-touch values populate the standard `utm_*` / `referrer` keys (what GA4
 * and Ads expect for the converting session); first-touch values ride along as
 * `first_utm_*` so acquisition can still be reported when the two differ.
 */
export function attributionForDataLayer(): Record<string, string> {
  const { first, last } = getAttribution();
  const out: Record<string, string> = {};

  const touch = last ?? first;
  if (touch) {
    for (const key of UTM_KEYS) {
      const value = touch[key];
      if (value) out[key] = value;
    }
    if (touch.referrer) out.referrer = touch.referrer;
    if (touch.landing_page) out.landing_page = touch.landing_page;
  }

  if (first) {
    for (const key of UTM_KEYS) {
      const value = first[key];
      if (value) out[`first_${key}`] = value;
    }
    if (first.referrer) out.first_referrer = first.referrer;
  }

  return out;
}

/**
 * Flat attribution payload for tracking relays (payment_events rows, Meta
 * custom_data). Identical shape to the dataLayer payload.
 */
export function attributionForServer(): Record<string, string> {
  return attributionForDataLayer();
}

/**
 * Full first + last touch record (with timestamps) saved on sign-ups,
 * assessments and payments. Last-touch keys fall back to the first touch when
 * no campaign has arrived since.
 */
export function attributionRecord(): Record<string, string> {
  // Also captures campaign parameters introduced by client-side navigation.
  captureAttribution();
  const { first, last } = getAttribution();
  const out: Record<string, string> = {};
  const touch = last ?? first;
  if (touch) {
    for (const key of UTM_KEYS) {
      const value = touch[key];
      if (value) out[key] = value;
    }
    if (touch.referrer) out.referrer = touch.referrer;
    if (touch.landing_page) out.landing_page = touch.landing_page;
    if (touch.captured_at) out.last_touch_at = touch.captured_at;
  }
  if (first) {
    for (const key of UTM_KEYS) {
      const value = first[key];
      if (value) { out[`first_${key}`] = value; out[key] = value; }
      else delete out[key];
    }
    if (first.referrer) out.first_referrer = first.referrer;
    if (first.referrer) out.referrer = first.referrer;
    else delete out.referrer;
    if (first.landing_page) out.first_landing_page = first.landing_page;
    if (first.landing_page) { out.landing_page = first.landing_page; out.landing_url = first.landing_page; }
    if (first.captured_at) out.first_touch_at = first.captured_at;
    if (first.captured_at) out.first_touch = first.captured_at;
  }
  return out;
}
